import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { audienceModeService } from '@/services/audienceModeService';
import type { AudienceModeSession } from '@/types/mediaOutlets';
import { toast } from 'sonner';
import { MapPin, Tv, Radio, Clock, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ActiveAudienceSessionProps {
  session: AudienceModeSession;
  onSessionEnded?: () => void;
}

export const ActiveAudienceSession: React.FC<ActiveAudienceSessionProps> = ({
  session,
  onSessionEnded
}) => {
  const [ending, setEnding] = useState(false);

  const handleEndSession = async () => {
    setEnding(true);
    try {
      const result = await audienceModeService.endSession(session.id);
      if (result.success) {
        toast.success('Session ended');
        onSessionEnded?.();
      } else {
        toast.error(result.error || 'Failed to end session');
      }
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error('An error occurred');
    } finally {
      setEnding(false);
    }
  };

  const getLocationIcon = () => {
    switch (session.location_type) {
      case 'onsite':
        return <MapPin className="w-5 h-5" />;
      case 'watching_in':
        return <Tv className="w-5 h-5" />;
      case 'listening_in':
        return <Radio className="w-5 h-5" />;
    }
  };

  const getLocationLabel = () => {
    switch (session.location_type) {
      case 'onsite':
        return 'At the Venue';
      case 'watching_in':
        return 'Watching on TV';
      case 'listening_in':
        return 'Listening on Radio';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              {getLocationIcon()}
            </div>
            <div>
              <CardTitle>Active Session</CardTitle>
              <CardDescription>
                Started {formatDistanceToNow(new Date(session.session_started_at), { addSuffix: true })}
              </CardDescription>
            </div>
          </div>
          <Badge variant="default" className="animate-pulse">
            Live
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-accent/50">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Location</div>
              <div className="text-lg">{getLocationLabel()}</div>
            </div>
          </div>

          {session.outlet_id && (
            <div className="p-4 rounded-lg border">
              <div className="text-sm font-medium text-muted-foreground mb-1">
                {session.location_type === 'watching_in' ? 'Channel' : 'Station'}
              </div>
              <div className="text-lg font-medium">
                {session.custom_outlet_name || 'Selected Outlet'}
              </div>
            </div>
          )}

          {session.is_custom_entry && session.custom_outlet_name && (
            <div className="p-4 rounded-lg border border-primary/20 bg-primary/5">
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Custom Submission
              </div>
              <div className="text-lg font-medium">{session.custom_outlet_name}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Your submission is under review
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={handleEndSession}
          disabled={ending}
          variant="destructive"
          className="w-full"
        >
          {ending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          End Session
        </Button>
      </CardContent>
    </Card>
  );
};
